/**
 * Volver arriba al cambiar de pantalla o de pestaña.
 *
 * El problema que resuelve: la que scrollea en esta app es la PÁGINA (ver
 * CabeceraFija — no hay cajas con scroll propio a propósito, para que la rueda
 * funcione con el cursor donde sea). Cambiar de pestaña es cambiar un estado,
 * no recargar nada, así que el navegador se queda exactamente donde estabas:
 * entras a una pestaña recién abierta y aparece por la mitad, o hasta el final
 * si la anterior era más larga. Lo mismo al navegar entre vistas — React Router
 * no toca el scroll.
 *
 * Se arregla en un solo sitio y no vista por vista: quien engancha esto es
 * `SegmentedControl` (todas las pestañas de sección de la app) más el par de
 * rieles de píldoras que no lo usan, y el cambio de ruta en App.tsx.
 *
 * NO lo enganchan los filtros. Marcar «Comercial» en Clientes es afinar lo que
 * ya estás mirando, y arrancarte hasta arriba mientras comparas dos filas es
 * peor que dejarte donde estás. Por eso `SegmentedControl` solo vuelve arriba
 * en su variante `underline`, que es la de pestañas; la `pill` es la de filtros.
 */
import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * La caja que hace scroll alrededor de un elemento, o `null` si es la página.
 *
 * Hace falta buscarla porque no todas las pestañas viven en el flujo de la
 * página: las de un panel lateral o las de un modal están dentro de una caja
 * con `overflow-y-auto` propia, y ahí lo que hay que devolver a cero es ESA
 * caja — mover la ventana no haría nada visible.
 *
 * La búsqueda para en `<body>` a propósito. Con `body { overflow-x: hidden }`
 * (index.css) el eje contrario computa `auto`, así que el body pasaría por
 * contenedor de scroll sin serlo: quien scrollea es el viewport, y ahí manda
 * `document.scrollingElement`. Escribirle `scrollTop` al body no movería nada.
 */
function contenedorDeScroll(desde: Element | null | undefined): Element | null {
    if (typeof document === 'undefined') return null;
    let el = desde?.parentElement ?? null;
    while (el && el !== document.body && el !== document.documentElement) {
        const { overflowY } = getComputedStyle(el);
        if (/^(auto|scroll|overlay)$/.test(overflowY) && el.scrollHeight > el.clientHeight + 1) {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}

/**
 * Deja la vista arriba del todo, al instante.
 *
 * Se escribe `scrollTop` en vez de llamar a `scrollTo`: es instantáneo pase lo
 * que pase (un `behavior: smooth` heredado convertiría esto en una animación
 * larguísima desde el final de una lista) y no arrastra el «Not implemented»
 * que jsdom escupe con `window.scrollTo` en las pruebas.
 *
 * @param desde Un elemento de la zona que se está cambiando —el riel de
 *   pestañas—, para acertar con su caja cuando no es la página.
 */
export function volverArriba(desde?: Element | null): void {
    if (typeof document === 'undefined') return;
    const caja = contenedorDeScroll(desde) ?? document.scrollingElement ?? document.documentElement;
    if (caja) caja.scrollTop = 0;
}

/**
 * Vuelve arriba cuando `clave` cambia. El primer render NO cuenta: al montar ya
 * estás donde el navegador te dejó, y un enlace profundo (`?section=pagos`)
 * llega con su valor puesto de salida, no como un cambio.
 */
export function useVolverArribaAlCambiar(
    clave: unknown,
    ancla?: RefObject<Element | null>,
): void {
    const anterior = useRef(clave);
    useEffect(() => {
        if (Object.is(anterior.current, clave)) return;
        anterior.current = clave;
        volverArriba(ancla?.current ?? null);
    }, [clave, ancla]);
}

/** Cambio de vista = pantalla nueva, y una pantalla nueva empieza por arriba. */
export function useVolverArribaAlNavegar(): void {
    const { pathname } = useLocation();
    useVolverArribaAlCambiar(pathname);
}
